/**
 * DatePicker — single date picker with year/month quick navigation.
 *
 * Native builds use the custom modal calendar. On web, the browser's native
 * date input is layered invisibly over the same branded field so Safari and
 * desktop browsers can edit a date directly, including typing the year.
 *
 * All dates ISO 8601 (YYYY-MM-DD).
 */

import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View, Text, Platform, ScrollView as RNScrollView } from 'react-native'
import { AppModal } from './AppModal'
import { GlassSheet } from './GlassSheet'
// Deep entry, never the `react-native-calendars` barrel: that barrel also
// exports Agenda, ExpandableCalendar and Timeline, which drag in the full
// lodash build, moment and recyclerlistview — ~190 KB of modules for
// components this app never renders. `src/calendar` ships its own .d.ts.
import RNCalendar from 'react-native-calendars/src/calendar'
import type { DateData } from 'react-native-calendars/src/types'
import { Calendar as CalendarIcon, X, ChevronLeft, ChevronRight } from '@/components/ui/icons'
import { selection } from '../../lib/utils/haptics'
import { TrackedNativePressable as RNPressable } from './TrackedNativePressable'
// Native (non-RNGH) Pressable/ScrollView by choice, not by workaround: the
// calendar grid wants plain RN hit-testing so a tap never has to win a gesture
// race against the day cells around it. `AppModal` supplies the
// GestureHandlerRootView, so RNGH components would work here too.
const Pressable = RNPressable
const ScrollView = RNScrollView
import { useTheme } from '../../lib/hooks/use-theme'
import { useFontScale } from '../../lib/hooks/use-font-scale'
import {
  useSemanticColors,
  RADIUS,
  SPACING,
  FORM_FIELD_GAP,
  INTERACTION,
} from '../../lib/theme/tokens'
import { COLORS, FONTS } from '../../lib/brand'
import { cn } from '../../lib/theme/variants'
import {
  formatSvDateDisplay,
  formatSvMonthYearDisplay,
  lastDayOfMonthISO,
} from '../../lib/utils/dates'

// ─── Types ──────────────────────────────────────────────────────────────────

interface DatePickerProps {
  /** Selected date (ISO string YYYY-MM-DD or null) */
  value: string | null
  /** Called with ISO date string */
  onChange: (date: string) => void
  /** Field label */
  label?: string
  /** Minimum selectable date (ISO string) */
  minDate?: string
  /** Maximum selectable date (ISO string) */
  maxDate?: string
  /** Dates to mark as unavailable */
  disabledDates?: string[]
  /** Error message */
  error?: string
  /** Disabled state */
  disabled?: boolean
  /** Additional NativeWind className */
  className?: string
  /** E2E selector for the trigger field */
  testID?: string
  /** Select a full day or a document-style month/year value stored as month-end ISO */
  mode?: 'date' | 'month'
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const FULL_MONTHS_ES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
]
const FULL_MONTHS_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function toISOMonth(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}-01`
}

type PickerView = 'calendar' | 'year' | 'month'

function addDaysISO(iso: string, days: number): string {
  const date = new Date(`${iso}T12:00:00`)
  date.setDate(date.getDate() + days)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function webTestIdProps(testID?: string) {
  if (Platform.OS !== 'web' || !testID) return null
  return { dataSet: { testid: testID } } as Record<string, unknown>
}

// ─── Year Picker Grid ───────────────────────────────────────────────────────

function YearPicker({
  minYear,
  maxYear,
  selectedYear,
  onSelect,
  colors,
}: {
  minYear: number
  maxYear: number
  selectedYear: number
  onSelect: (year: number) => void
  colors: ReturnType<typeof useSemanticColors>
}) {
  const years: number[] = []
  for (let y = maxYear; y >= minYear; y--) years.push(y)

  return (
    <ScrollView
      style={{ maxHeight: 300 }}
      contentContainerStyle={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        padding: SPACING.sm,
        gap: SPACING.sm,
      }}
      showsVerticalScrollIndicator={false}
    >
      {years.map((y) => {
        const isSelected = y === selectedYear
        return (
          <Pressable
            key={y}
            onPress={() => onSelect(y)}
            accessibilityRole="button"
            accessibilityLabel={String(y)}
            accessibilityState={{ selected: isSelected }}
            style={{
              width: '22%',
              paddingVertical: SPACING.sm + 2,
              borderRadius: RADIUS.sm,
              alignItems: 'center',
              backgroundColor: isSelected ? COLORS.coral.DEFAULT : 'transparent',
            }}
          >
            <Text
              style={{
                fontSize: 15,
                fontFamily: isSelected ? FONTS.bodyBold : FONTS.body,
                color: isSelected ? '#FFFFFF' : colors.textPrimary,
              }}
            >
              {y}
            </Text>
          </Pressable>
        )
      })}
    </ScrollView>
  )
}

// ─── Month Picker Grid ──────────────────────────────────────────────────────

function MonthPicker({
  selectedMonth,
  selectedYear,
  minDate,
  maxDate,
  onSelect,
  colors,
  locale,
}: {
  selectedMonth: number
  selectedYear: number
  minDate?: string
  maxDate?: string
  onSelect: (month: number) => void
  colors: ReturnType<typeof useSemanticColors>
  locale: string
}) {
  const monthNames = locale.startsWith('es') ? FULL_MONTHS_ES : FULL_MONTHS_EN

  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', padding: SPACING.sm, gap: SPACING.sm }}>
      {monthNames.map((name, index) => {
        const month = index + 1
        const isSelected = month === selectedMonth
        const monthEndISO = lastDayOfMonthISO(selectedYear, month)
        const monthStartISO = toISOMonth(selectedYear, month)
        const isDisabled =
          Boolean(minDate && monthEndISO < minDate) || Boolean(maxDate && monthStartISO > maxDate)
        return (
          <Pressable
            key={month}
            onPress={() => {
              if (!isDisabled) onSelect(month)
            }}
            disabled={isDisabled}
            accessibilityRole="button"
            accessibilityLabel={name}
            accessibilityState={{ disabled: isDisabled, selected: isSelected }}
            style={{
              width: '30%',
              paddingVertical: SPACING.sm + SPACING.xs,
              borderRadius: RADIUS.sm,
              alignItems: 'center',
              backgroundColor: isSelected ? COLORS.coral.DEFAULT : 'transparent',
              // 0.4 measured 2.15:1 — a date you cannot pick should still be
              // readable. Same value the other calendars settled on.
              opacity: isDisabled ? 0.55 : 1,
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontFamily: isSelected ? FONTS.bodyBold : FONTS.body,
                color: isSelected ? '#FFFFFF' : colors.textPrimary,
              }}
              numberOfLines={1}
            >
              {name}
            </Text>
          </Pressable>
        )
      })}
    </View>
  )
}

// ─── DatePicker ─────────────────────────────────────────────────────────────

export function DatePicker({
  value,
  onChange,
  label,
  minDate,
  maxDate,
  disabledDates,
  error,
  disabled = false,
  className,
  testID,
  mode = 'date',
}: DatePickerProps) {
  const { t, i18n } = useTranslation()
  // The resting state of a date field belongs to the date field.
  //
  // This used to be a prop, and the five call sites produced four different
  // conventions: "DD/MM/AAAA", "MM/AAAA", "Seleccioná tu fecha de nacimiento"
  // and "Seleccionar fecha". The first two are typing masks on a control that
  // cannot be typed into — they advertise a keyboard that never appears, in a
  // form where every other placeholder is a friendly example. The third is a
  // sentence that does not fit one line on a 360pt phone, so it ellipsised.
  //
  // The label above already says WHICH date this is; the placeholder only has
  // to say what tapping does. Passing one is no longer possible, which is what
  // stops the four conventions from growing back into five.
  const resolvedPlaceholder = mode === 'month' ? t('ui.select_month') : t('ui.select_date')
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  // Same contract as TextInput: the field floor moves with its own text.
  const { box } = useFontScale()
  const locale = i18n.language

  const [isOpen, setIsOpen] = useState(false)
  const [pickerView, setPickerView] = useState<PickerView>('calendar')

  // The month currently displayed in the calendar (not the selected date)
  const [displayedYear, setDisplayedYear] = useState(() => {
    if (value) return parseInt(value.split('-')[0]!, 10)
    if (maxDate) return parseInt(maxDate.split('-')[0]!, 10)
    return new Date().getFullYear()
  })
  const [displayedMonth, setDisplayedMonth] = useState(() => {
    if (value) return parseInt(value.split('-')[1]!, 10)
    if (maxDate) return parseInt(maxDate.split('-')[1]!, 10)
    return new Date().getMonth() + 1
  })

  // Year range
  const minYear = minDate ? parseInt(minDate.split('-')[0]!, 10) : 1920
  const maxYear = maxDate ? parseInt(maxDate.split('-')[0]!, 10) : new Date().getFullYear() + 1

  const currentISO = toISOMonth(displayedYear, displayedMonth)
  const today = useMemo(() => {
    const now = new Date()
    const y = now.getFullYear()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    const d = String(now.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }, [])
  const tomorrow = useMemo(() => addDaysISO(today, 1), [today])

  const markedDates = useMemo(() => {
    const marks: Record<
      string,
      {
        selected?: boolean
        selectedColor?: string
        disabled?: boolean
        disableTouchEvent?: boolean
      }
    > = {}
    if (value) {
      marks[value] = { selected: true, selectedColor: COLORS.coral.DEFAULT }
    }
    disabledDates?.forEach((d) => {
      marks[d] = { ...marks[d], disabled: true, disableTouchEvent: true }
    })
    return marks
  }, [value, disabledDates])

  const calendarTheme = useMemo(
    () => ({
      backgroundColor: 'transparent',
      calendarBackground: 'transparent',
      textSectionTitleColor: colors.textSecondary,
      selectedDayBackgroundColor: COLORS.coral.DEFAULT,
      selectedDayTextColor: '#FFFFFF',
      todayTextColor: COLORS.purple.DEFAULT,
      dayTextColor: colors.textPrimary,
      textDisabledColor: colors.textDisabled,
      arrowColor: colors.primary,
      monthTextColor: colors.textPrimary,
      textDayFontWeight: '400' as const,
      textMonthFontWeight: '600' as const,
      textDayHeaderFontWeight: '600' as const,
      textDayFontSize: 14,
      textMonthFontSize: 16,
      textDayHeaderFontSize: 12,
    }),
    [colors],
  )

  const handleDayPress = useCallback(
    (day: DateData) => {
      selection()
      onChange(day.dateString)
      // Delay close to let parent process onChange before Modal unmounts —
      // prevents state race that blocks reopening on native
      requestAnimationFrame(() => {
        setIsOpen(false)
        setPickerView('calendar')
      })
    },
    [onChange],
  )

  const handleMonthValueSelect = useCallback(
    (month: number) => {
      selection()
      onChange(lastDayOfMonthISO(displayedYear, month))
      requestAnimationFrame(() => {
        setIsOpen(false)
        setPickerView('calendar')
      })
    },
    [displayedYear, onChange],
  )

  const renderDay = useCallback(
    ({ date, state }: { date?: DateData; state?: string }) => {
      if (!date) return <View style={{ width: 40, height: 40 }} />

      const dateString = date.dateString
      const isDisabled =
        state === 'disabled' ||
        Boolean(minDate && dateString < minDate) ||
        Boolean(maxDate && dateString > maxDate) ||
        Boolean(disabledDates?.includes(dateString))
      const isSelected = value === dateString
      const dayTestID =
        dateString === today
          ? 'date-picker.today'
          : dateString === tomorrow
            ? 'date-picker.tomorrow'
            : `date-picker.day-${dateString}`

      return (
        <Pressable
          onPress={() => {
            if (!isDisabled) handleDayPress(date)
          }}
          disabled={isDisabled}
          accessibilityRole="button"
          accessibilityLabel={String(date.day)}
          accessibilityState={{ disabled: isDisabled, selected: isSelected }}
          testID={dayTestID}
          {...webTestIdProps(dayTestID)}
          style={{
            // A calendar day is a control; 7 of them fit at 44 on a 390px
            // phone with the screen's own padding.
            minWidth: INTERACTION.minTouchTarget,
            minHeight: INTERACTION.minTouchTarget,
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isDisabled ? 0.4 : 1,
          }}
        >
          <View
            style={{
              width: 34,
              height: 34,
              borderRadius: 17,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isSelected ? COLORS.coral.DEFAULT : 'transparent',
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontFamily: isSelected ? FONTS.bodyBold : FONTS.body,
                color: isSelected
                  ? '#FFFFFF'
                  : isDisabled
                    ? colors.textDisabled
                    : colors.textPrimary,
              }}
            >
              {date.day}
            </Text>
          </View>
        </Pressable>
      )
    },
    [
      colors.textDisabled,
      colors.textPrimary,
      disabledDates,
      handleDayPress,
      maxDate,
      minDate,
      today,
      tomorrow,
      value,
    ],
  )

  const handlePrevMonth = useCallback(() => {
    if (displayedMonth === 1) {
      setDisplayedYear((y) => y - 1)
      setDisplayedMonth(12)
    } else {
      setDisplayedMonth((m) => m - 1)
    }
  }, [displayedMonth])

  const handleNextMonth = useCallback(() => {
    if (displayedMonth === 12) {
      setDisplayedYear((y) => y + 1)
      setDisplayedMonth(1)
    } else {
      setDisplayedMonth((m) => m + 1)
    }
  }, [displayedMonth])

  const handleYearSelect = useCallback(
    (year: number) => {
      setDisplayedYear(year)
      setPickerView(mode === 'month' ? 'month' : 'calendar')
    },
    [mode],
  )

  const handleMonthSelect = useCallback(
    (month: number) => {
      if (mode === 'month') {
        handleMonthValueSelect(month)
        return
      }
      setDisplayedMonth(month)
      setPickerView('calendar')
    },
    [handleMonthValueSelect, mode],
  )

  const handlePrevPeriod = useCallback(() => {
    if (mode === 'month') {
      setDisplayedYear((y) => Math.max(minYear, y - 1))
      return
    }
    handlePrevMonth()
  }, [handlePrevMonth, minYear, mode])

  const handleNextPeriod = useCallback(() => {
    if (mode === 'month') {
      setDisplayedYear((y) => Math.min(maxYear, y + 1))
      return
    }
    handleNextMonth()
  }, [handleNextMonth, maxYear, mode])

  const handleOpen = useCallback(() => {
    if (disabled) return
    // Reset displayed date to value or maxDate (for birthdate UX — start near their likely age)
    if (value) {
      setDisplayedYear(parseInt(value.split('-')[0]!, 10))
      setDisplayedMonth(parseInt(value.split('-')[1]!, 10))
    } else if (maxDate) {
      setDisplayedYear(parseInt(maxDate.split('-')[0]!, 10))
      setDisplayedMonth(parseInt(maxDate.split('-')[1]!, 10))
    }
    setPickerView(mode === 'month' ? 'month' : 'calendar')
    setIsOpen(true)
  }, [disabled, value, maxDate, mode])

  const displayValue = value
    ? mode === 'month'
      ? formatSvMonthYearDisplay(value)
      : formatSvDateDisplay(value)
    : ''
  const usesNativeWebInput = Platform.OS === 'web' && mode === 'date'

  const monthNames = locale.startsWith('es') ? FULL_MONTHS_ES : FULL_MONTHS_EN

  // ── Renderer ─────────────────────────────────────────────────────────────
  //
  // The branded field remains the visual surface everywhere. On web, a
  // transparent native input receives the tap; on iOS/Android, the custom
  // modal provides the year/month quick-jump controls.

  return (
    <View className={cn(className)} style={{ marginBottom: FORM_FIELD_GAP }}>
      {label ? (
        <Text
          style={{
            fontSize: 14,
            fontFamily: FONTS.body,
            color: colors.textPrimary,
            opacity: 0.75,
            marginBottom: SPACING.md,
            letterSpacing: 0.2,
          }}
        >
          {label}
        </Text>
      ) : null}

      <View style={{ position: 'relative' }}>
        <Pressable
          onPress={handleOpen}
          disabled={disabled || usesNativeWebInput}
          testID={usesNativeWebInput ? undefined : testID}
          {...webTestIdProps(usesNativeWebInput ? undefined : testID)}
          accessibilityRole="button"
          accessibilityLabel={label ?? resolvedPlaceholder}
          pointerEvents={usesNativeWebInput ? 'none' : 'auto'}
          style={({ pressed }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            minHeight: box(INTERACTION.fieldHeight),
            paddingHorizontal: SPACING.md,
            paddingVertical: SPACING.md,
            borderRadius: RADIUS.md,
            borderWidth: 1,
            borderColor: error ? colors.error : colors.border,
            backgroundColor: colors.surfaceSubtle,
            opacity: disabled ? 0.4 : pressed ? 0.76 : 1,
          })}
        >
          <CalendarIcon
            size={18}
            color={colors.textSecondary}
            style={{ marginRight: INTERACTION.fieldIconGap }}
          />
          <Text
            style={{
              flex: 1,
              fontSize: 15,
              fontFamily: FONTS.body,
              color: displayValue ? colors.textPrimary : colors.textMuted,
            }}
            numberOfLines={1}
          >
            {displayValue || resolvedPlaceholder}
          </Text>
        </Pressable>

        {usesNativeWebInput
          ? React.createElement('input', {
              type: 'date',
              value: value ?? '',
              min: minDate,
              max: maxDate,
              disabled,
              'aria-label': label ?? resolvedPlaceholder,
              'data-testid': testID ? `${testID}.native-input` : undefined,
              onChange: (event: { target: { value: string } }) => {
                const nextValue = event.target.value
                if (!nextValue) return
                selection()
                onChange(nextValue)
              },
              style: {
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: disabled ? 'default' : 'pointer',
              },
            })
          : null}
      </View>

      {error ? (
        <Text
          style={{
            fontSize: 12,
            fontFamily: FONTS.body,
            color: colors.textError,
            marginTop: SPACING.xs,
          }}
        >
          {error}
        </Text>
      ) : null}

      {/* Modal */}
      <AppModal
        visible={isOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setIsOpen(false)}
      >
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING.lg,
          }}
        >
          <Pressable
            onPress={() => {
              setIsOpen(false)
              setPickerView('calendar')
            }}
            accessibilityRole="button"
            accessibilityLabel={t('common.close')}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            }}
          />
          <GlassSheet
            style={{
              width: '100%',
              maxWidth: 400,
            }}
          >
            {/* Header with close */}
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingHorizontal: SPACING.md + 4,
                paddingTop: SPACING.md,
                paddingBottom: SPACING.sm,
              }}
            >
              <Text style={{ fontSize: 16, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
                {label ?? resolvedPlaceholder}
              </Text>
              <Pressable
                onPress={() => {
                  setIsOpen(false)
                  setPickerView('calendar')
                }}
                hitSlop={8}
                accessibilityRole="button"
                accessibilityLabel={t('common.cancel')}
                testID={testID ? `${testID}.close` : 'datepicker.close'}
              >
                <X size={20} color={colors.textSecondary} strokeWidth={2} />
              </Pressable>
            </View>

            {/* Custom month/year navigation */}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                paddingHorizontal: SPACING.md,
                paddingBottom: SPACING.sm,
                gap: SPACING.sm,
              }}
            >
              <Pressable
                onPress={handlePrevPeriod}
                accessibilityLabel={
                  mode === 'month'
                    ? t('a11y.datepicker_prev_year')
                    : t('a11y.datepicker_prev_month')
                }
                accessibilityRole="button"
                testID={testID ? `${testID}.prev-period` : 'datepicker.prev-period'}
                style={{ padding: SPACING.sm }}
              >
                <ChevronLeft size={20} color={colors.textSecondary} />
              </Pressable>

              {mode === 'date' && (
                <Pressable
                  onPress={() => setPickerView(pickerView === 'month' ? 'calendar' : 'month')}
                  accessibilityRole="button"
                  accessibilityLabel={t('a11y.datepicker_select_month')}
                  testID={testID ? `${testID}.select-month` : 'datepicker.select-month'}
                  style={{
                    paddingVertical: SPACING.xs,
                    paddingHorizontal: SPACING.sm,
                    borderRadius: RADIUS.sm,
                    backgroundColor: pickerView === 'month' ? colors.surfaceSubtle : 'transparent',
                  }}
                >
                  <Text
                    style={{ fontSize: 15, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}
                  >
                    {monthNames[displayedMonth - 1]}
                  </Text>
                </Pressable>
              )}

              <Pressable
                onPress={() => setPickerView(pickerView === 'year' ? 'calendar' : 'year')}
                accessibilityRole="button"
                accessibilityLabel={t('a11y.datepicker_select_year')}
                testID={testID ? `${testID}.select-year` : 'datepicker.select-year'}
                style={{
                  paddingVertical: SPACING.xs,
                  paddingHorizontal: SPACING.sm,
                  borderRadius: RADIUS.sm,
                  backgroundColor: pickerView === 'year' ? colors.surfaceSubtle : 'transparent',
                }}
              >
                <Text
                  style={{ fontSize: 15, fontFamily: FONTS.bodyBold, color: colors.textAccent }}
                >
                  {displayedYear}
                </Text>
              </Pressable>

              <Pressable
                onPress={handleNextPeriod}
                accessibilityLabel={
                  mode === 'month'
                    ? t('a11y.datepicker_next_year')
                    : t('a11y.datepicker_next_month')
                }
                accessibilityRole="button"
                testID={testID ? `${testID}.next-period` : 'datepicker.next-period'}
                style={{ padding: SPACING.sm }}
              >
                <ChevronRight size={20} color={colors.textSecondary} />
              </Pressable>
            </View>

            {/* Content: calendar, year picker, or month picker */}
            <View style={{ minHeight: 320 }}>
              {pickerView === 'year' && (
                <YearPicker
                  minYear={minYear}
                  maxYear={maxYear}
                  selectedYear={displayedYear}
                  onSelect={handleYearSelect}
                  colors={colors}
                />
              )}

              {pickerView === 'month' && (
                <MonthPicker
                  selectedMonth={displayedMonth}
                  selectedYear={displayedYear}
                  minDate={minDate}
                  maxDate={maxDate}
                  onSelect={handleMonthSelect}
                  colors={colors}
                  locale={locale}
                />
              )}

              {pickerView === 'calendar' && (
                <View style={{ paddingHorizontal: SPACING.md, paddingBottom: SPACING.md }}>
                  <RNCalendar
                    key={currentISO}
                    current={currentISO}
                    minDate={minDate}
                    maxDate={maxDate}
                    markedDates={markedDates}
                    onDayPress={handleDayPress}
                    dayComponent={renderDay}
                    theme={calendarTheme}
                    hideArrows
                    hideExtraDays={false}
                    renderHeader={() => null}
                  />
                </View>
              )}
            </View>
          </GlassSheet>
        </View>
      </AppModal>
    </View>
  )
}
